// frontend/src/pages/admin/SystemUsers.jsx
import { useState, useEffect } from 'react';
import { systemUserAPI } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { useAuth } from '../../context/AuthContext';

const SystemUsers = () => {
  const { hasPermission, permissions: myPermissions, user: currentUser } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    phone: '',
    permissions: []
  });

  // Эрхүүд
  const canCreate = hasPermission('system.users.create');
  const canEdit = hasPermission('system.users.edit');
  const canDelete = hasPermission('system.users.delete');
  const canManagePermissions = hasPermission('system.users.permissions');

  useEffect(() => {
    fetchUsers();
    fetchPermissions();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await systemUserAPI.getAll();
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (err) {
      setError('Хэрэглэгчдийн жагсаалт татахад алдаа гарлаа');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await systemUserAPI.getPermissions();
      if (response.data.success) {
        setPermissions(response.data.data);
      }
    } catch (err) {
      console.error('Permissions fetch error:', err);
    }
  };

  // Өөрийн эрхтэй эсэхийг шалгах (Super admin биш бол)
  const canGrantPermission = (permKey) => {
    // Super admin бол бүх эрхийг өгч болно
    if (currentUser?.is_super_admin) return true;
    // Өөрт байгаа эрхийг л өгч болно
    return myPermissions.includes(permKey);
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      if (!canEdit) return;
      setEditingUser(user);
      setFormData({
        username: user.username,
        password: '',
        full_name: user.full_name,
        email: user.email || '',
        phone: user.phone || '',
        permissions: user.permissions || []
      });
    } else {
      if (!canCreate) return;
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        full_name: '',
        email: '',
        phone: '',
        permissions: []
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      username: '',
      password: '',
      full_name: '',
      email: '',
      phone: '',
      permissions: []
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePermissionChange = (permKey) => {
    // Өөрт байхгүй эрхийг өгөх боломжгүй
    if (!canGrantPermission(permKey)) return;
    
    setFormData(prev => {
      const perms = prev.permissions.includes(permKey)
        ? prev.permissions.filter(p => p !== permKey)
        : [...prev.permissions, permKey];
      return { ...prev, permissions: perms };
    });
  };

  const handleSelectAllModule = (modulePerms, isSelected) => {
    setFormData(prev => {
      let newPerms = [...prev.permissions];
      modulePerms.forEach(p => {
        // Зөвхөн өөрт байгаа эрхийг л өгч болно
        if (!canGrantPermission(p.key)) return;
        
        if (isSelected) {
          if (!newPerms.includes(p.key)) newPerms.push(p.key);
        } else {
          newPerms = newPerms.filter(np => np !== p.key);
        }
      });
      return { ...prev, permissions: newPerms };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.full_name) {
      alert('Хэрэглэгчийн нэр болон бүтэн нэр заавал шаардлагатай');
      return;
    }
    
    if (!editingUser && !formData.password) {
      alert('Нууц үг заавал шаардлагатай');
      return;
    }

    // Өөрт байхгүй эрхийг шүүж хасах
    const allowedPermissions = formData.permissions.filter(p => canGrantPermission(p));

    try {
      setSaving(true);
      
      const dataToSend = {
        ...formData,
        permissions: allowedPermissions,
        password: formData.password || undefined
      };
      
      if (editingUser) {
        await systemUserAPI.update(editingUser.id, dataToSend);
        alert('Хэрэглэгч амжилттай шинэчлэгдлээ');
      } else {
        await systemUserAPI.create(dataToSend);
        alert('Хэрэглэгч амжилттай үүсгэлээ');
      }
      
      handleCloseModal();
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!canDelete) return;
    
    if (user.is_super_admin) {
      alert('Super admin-г устгах боломжгүй');
      return;
    }
    
    if (!confirm(`"${user.full_name}" хэрэглэгчийг устгахдаа итгэлтэй байна уу?`)) {
      return;
    }

    try {
      await systemUserAPI.delete(user.id);
      alert('Хэрэглэгч амжилттай устгагдлаа');
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  const handleViewUser = async (userId) => {
    if (!canEdit) return;
    
    try {
      const response = await systemUserAPI.getById(userId);
      if (response.data.success) {
        handleOpenModal(response.data.data);
      }
    } catch (err) {
      alert('Хэрэглэгчийн мэдээлэл татахад алдаа гарлаа');
    }
  };

  const getModuleName = (module) => {
  const names = {
    'system_dashboard': '📊 Dashboard',
    'system_companies': '🏢 Компани',
    'system_settlements': '💰 Төлбөр тооцоо',
    'system_reports': '📈 Тайлан',
    'system_settings': '⚙️ Тохиргоо',
    'system_users': '👥 Хэрэглэгч',
    'system_login_settings': '🎨 Login хуудас'  // ← НЭМЭХ
  };
  return names[module] || module;
};

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('mn-MN');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">👥 Хэрэглэгчийн удирдлага</h1>
            <p className="text-gray-600 mt-1">Системийн админ хэрэглэгчдийг удирдах</p>
          </div>
          {/* Бүртгэх эрхтэй бол л товч харуулах */}
          {canCreate && (
            <button
              onClick={() => handleOpenModal()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
            >
              <span>➕</span>
              <span>Хэрэглэгч нэмэх</span>
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Хэрэглэгч</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Холбоо барих</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Төлөв</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Үүсгэсэн</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Сүүлд нэвтэрсэн</th>
                  {(canEdit || canDelete) && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Үйлдэл</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={canEdit || canDelete ? 6 : 5} className="px-6 py-12 text-center text-gray-500">
                      Хэрэглэгч байхгүй байна
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold">
                            {user.full_name?.charAt(0) || 'U'}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 flex items-center gap-2">
                              {user.full_name}
                              {user.is_super_admin && (
                                <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                                  Super Admin
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">@{user.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{user.email || '-'}</div>
                        <div className="text-sm text-gray-500">{user.phone || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.status === 'active' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {user.status === 'active' ? 'Идэвхтэй' : 'Идэвхгүй'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                         <div>{user.createdBy?.full_name || '-'}</div>
                        <div className="text-xs">{formatDate(user.created_at)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.last_login ? formatDate(user.last_login) : 'Нэвтрээгүй'}
                      </td>
                      {(canEdit || canDelete) && (
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            {canEdit && (
                              <button
                                onClick={() => handleViewUser(user.id)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                title="Засах"
                              >
                                ✏️
                              </button>
                            )}
                            {canDelete && !user.is_super_admin && (
                              <button
                                onClick={() => handleDelete(user)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                title="Устгах"
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (canCreate || canEdit) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {editingUser ? '✏️ Хэрэглэгч засах' : '➕ Шинэ хэрэглэгч'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Хэрэглэгчийн нэр <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      disabled={!!editingUser}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                      placeholder="username"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Нууц үг {!editingUser && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={editingUser ? 'Хоосон үлдээвэл өөрчлөгдөхгүй' : '••••••••'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Бүтэн нэр <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Овог Нэр"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Имэйл
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="email@example.com"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Утас
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="99001122"
                    />
                  </div>
                </div>

                {/* Permissions - Эрх удирдах эрхтэй бол л харуулах */}
                {canManagePermissions && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">🔐 Эрхүүд</h3>
                    {!currentUser?.is_super_admin && (
                      <p className="text-sm text-amber-600 mb-4 bg-amber-50 p-2 rounded-lg">
                        ⚠️ Та зөвхөн өөрт байгаа эрхийг л бусдад олгох боломжтой
                      </p>
                    )}
                    <div className="space-y-4">
                      {Object.entries(permissions).map(([module, perms]) => {
                        // Өөрт байгаа эрхүүдийг шүүх
                        const grantablePerms = perms.filter(p => canGrantPermission(p.key));
                        
                        // Хэрэв энэ модульд өгөх боломжтой эрх байхгүй бол харуулахгүй
                        if (grantablePerms.length === 0) return null;
                        
                        const allSelected = grantablePerms.every(p => formData.permissions.includes(p.key));
                        const someSelected = grantablePerms.some(p => formData.permissions.includes(p.key));
                        
                        return (
                          <div key={module} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-gray-900">{getModuleName(module)}</h4>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={allSelected}
                                  ref={el => {
                                    if (el) el.indeterminate = someSelected && !allSelected;
                                  }}
                                  onChange={(e) => handleSelectAllModule(grantablePerms, e.target.checked)}
                                  className="w-4 h-4 text-blue-600 rounded"
                                />
                                <span className="text-sm text-gray-600">Бүгд</span>
                              </label>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {perms.map((perm) => {
                                const canGrant = canGrantPermission(perm.key);
                                return (
                                  <label 
                                    key={perm.key} 
                                    className={`flex items-center gap-2 p-2 rounded ${
                                      canGrant 
                                        ? 'cursor-pointer hover:bg-gray-50' 
                                        : 'cursor-not-allowed opacity-50'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={formData.permissions.includes(perm.key)}
                                      onChange={() => handlePermissionChange(perm.key)}
                                      disabled={!canGrant}
                                      className="w-4 h-4 text-blue-600 rounded disabled:opacity-50"
                                    />
                                    <span className="text-sm text-gray-700">{perm.name}</span>
                                    {!canGrant && (
                                      <span className="text-xs text-gray-400">(эрхгүй)</span>
                                    )}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
                >
                  Болих
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Хадгалж байна...</span>
                    </>
                  ) : (
                    <span>{editingUser ? 'Хадгалах' : 'Үүсгэх'}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default SystemUsers;